import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import {
  AddUserIcon,
  AddUsersTwoIcon,
  BuildingThreeIcon,
  PlusIcon,
  PrintIcon,
  UploadIcon,
  UserCheckIcon,
  UserTwoIcon
} from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { assignLecResponsable } from '../../api/centers/assign-lec-responsable'
import { requestStaffs } from '../../api/centers/request-staffs'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import AssignLecResponsableForm from '../../components/pages/centers/assign-lec-responsable-form'
import { CentersFilter } from '../../components/pages/centers/center-filter'
import CenterLock from '../../components/pages/centers/center-lock'
import RequestStaffForm from '../../components/pages/centers/request-staff-form'
import { CenterCapsuleInformation } from '../../components/pages/centers/shared/center-capsule-information'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../lib/types/enums/staff/staff-qualifications.enum'
import { CenterCompact, CenterVersions } from '../../lib/types/models/center/center'
import { CenterFilter } from '../../lib/types/models/center/center-filter'
import { JourneyProperties } from '../../lib/types/models/product/journey.properties'
import { TripProperties } from '../../lib/types/models/trip/properties.trip'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformCentersFiltersToUrl,
  transformUrlToCenterFilters
} from '../../lib/utils/filters-mapping/centers'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { t } from '../../locale/utils'
import { getOlName } from '../ols/center/utils/functions'
import { constructCenterPath } from './utils'

const CenterListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const tableRef = useRef<DynamicTableRef<CenterCompact>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<CenterFilter>({
      initialFilters: {
        year: new Date().getFullYear()
      },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformCentersFiltersToUrl,
      transformUrlToFilter: transformUrlToCenterFilters
    })

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'filters' | 'request-staff' | 'assign-lec-responsable',
    CenterCompact[]
  >(['filters', 'request-staff', 'assign-lec-responsable'])

  const { mutateAsync: requestStaffsMutation } = useMutation({
    mutationFn: requestStaffs,
    mutationKey: ['request-staffs'],
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Demande de poste spécial créée avec succès')
      closeModel('request-staff')
    }
  })

  const { mutateAsync: assignLecResponsableMutation } = useMutation({
    mutationFn: assignLecResponsable,
    mutationKey: ['assign-lec-responsable'],
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      toast.success('Responsable secteur assigné avec succès')
      closeModel('assign-lec-responsable')
    }
  })

  const columns: Column<CenterCompact>[] = [
    {
      accessorKey: 'name',
      header: 'NOM du centre',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex flex-col gap-2 py-4 px-2">
          <div className="flex items-center gap-2">
            <CenterLock
              centerId={row.original.id}
              centerName={row.original.name}
              centerLocked={row.original.locked}
            />
            <Typography variant="secondary-body">{row.original.name}</Typography>
          </div>
          <CenterCapsuleInformation
            attributedPlaces={row.original.affectedStudentsNumber ?? 0}
            blockedPlaces={row.original.blockedPlacesNumber ?? 0}
            total={row.original.maxNumberOfPlaces ?? 0}
          />
        </div>
      )
    },
    {
      accessorKey: 'id',
      header: 'Pays',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.journeyPlace?.country ? (
          <CountryFlag
            possibleCountry={row.original.journeyPlace?.country.name}
            code={row.original.journeyPlace?.country.code}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'id',
      header: 'Dates',
      cell: ({ row }) => {
        const journeys = row.original.centerToJourneys.map(
          (centerToJourney) => centerToJourney.journey
        )
        return (
          <div className="flex flex-col gap-2">
            {journeys.map((journey) => {
              const period = journey.date.period
              const date = `${DateFormatter.DDMMYYYY(journey?.date.departureDate)} - ${DateFormatter.DDMMYYYY(journey?.date.returnDate)}`
              if (!period) return <Badge key={journey.id} text={date} />
              return <PeriodBadge key={journey.id} period={period} text={date} />
            })}
          </div>
        )
      }
    },
    {
      accessorKey: 'id',
      header: 'Séjours',
      cell: ({ row }) => {
        const centerToJourneys = row.original.centerToJourneys
        return (
          <div className="flex flex-col gap-2 py-2">
            {centerToJourneys.map((centerToJourney) => (
              <div key={centerToJourney.journey.id}>
                <Typography variant="label-semibold">
                  {JourneyProperties.getJourneyLabel(centerToJourney.journey)}
                </Typography>
                <div className="h-1" />
                <CenterCapsuleInformation
                  attributedPlaces={centerToJourney.affectedStudentsNumber ?? 0}
                  blockedPlaces={centerToJourney.blockedPlacesNumber ?? 0}
                  total={centerToJourney.maxNumberOfStudentPlaces}
                />
              </div>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'id',
      header: 'Voyages principaux',
      cell: ({ row }) => {
        const tripPlacesRequests = row.original.tripPlacesRequests
        return (
          <div className="flex flex-col gap-2 py-2">
            {tripPlacesRequests.map((tripPlacesRequest) => (
              <div key={tripPlacesRequest.trip.id}>
                <Typography variant="label-semibold">
                  {TripProperties.getTripLabel(tripPlacesRequest.trip)}
                </Typography>
                <div className="h-1" />
                <CenterCapsuleInformation
                  attributedPlaces={tripPlacesRequest.numberOfStudentsPlacesAffected ?? 0}
                  blockedPlaces={tripPlacesRequest.numberOfBlockedPlaces ?? 0}
                  total={tripPlacesRequest.numberOfStudentsPlacesRequested}
                />
              </div>
            ))}
          </div>
        )
      }
    },
    {
      accessorKey: 'staffRequests',
      header: 'Encadrants cours',
      cell: ({ row }) => {
        const staffRequests = row.original.staffRequests.filter(
          (staffRequest) =>
            staffQualificationEnumMapping[staffRequest.qualification].profession === 'course'
        )

        const grouped = staffRequests.reduce<Record<StaffQualificationEnum, number>>(
          (acc, req) => {
            const qualification = req.qualification
            acc[qualification] = (acc[qualification] || 0) + req.numberOfStaff
            return acc
          },
          {} as Record<StaffQualificationEnum, number>
        )

        return (
          <div className="flex flex-col gap-2 py-2">
            {Object.entries(grouped).map(([qualification, total]) => {
              const q = qualification as StaffQualificationEnum
              const qualificationData = staffQualificationEnumMapping[q]

              return (
                <div key={q} className="flex flex-col gap-2">
                  <Badge
                    text={`${t(qualificationData.label)} : ${total}`}
                    textVariant="label-semibold"
                    color="transparent"
                    textColor={
                      qualificationData.type === 'french'
                        ? tailwindColors.fushia.DEFAULT
                        : tailwindColors.purple.DEFAULT
                    }
                    borderColor={tailwindColors.grey.light.DEFAULT}
                  />
                </div>
              )
            })}
          </div>
        )
      }
    },
    {
      accessorKey: 'id',
      header: 'Encadrants activités',
      cell: ({ row }) => (
        <div className="flex flex-col gap-2">
          {row.original.staffRequests
            .filter(
              (staffRequest) =>
                staffQualificationEnumMapping[staffRequest.qualification].profession === 'activity'
            )
            .map((staffRequest) => (
              <div key={staffRequest.id} className="flex flex-col gap-2">
                <Badge
                  text={`${t(staffQualificationEnumMapping[staffRequest.qualification].label)} : ${staffRequest.numberOfStaff}`}
                  textVariant="label-semibold"
                  color="transparent"
                  textColor={
                    staffQualificationEnumMapping[staffRequest.qualification].type === 'french'
                      ? tailwindColors.fushia.DEFAULT
                      : tailwindColors.purple.DEFAULT
                  }
                  borderColor={tailwindColors.grey.light.DEFAULT}
                />
              </div>
            ))}
        </div>
      )
    },
    {
      accessorKey: 'ol.mainContactLastName',
      header: 'Prestataire étranger',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.ol, (ol) => getOlName(ol))
    },
    {
      accessorKey: 'rad',
      header: 'RAD',
      enableSorting: true,
      cell: ({ row }) =>
        !!row.original.rad ? <Badge text={DateFormatter.DDMMYYYY(row.original.rad.date)} /> : '-'
    },
    {
      accessorKey: 'lecResponsable.firstName',
      header: 'Responsable secteur',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.lecResponsable
          ? `${row.original.lecResponsable.lastName.toUpperCase()} ${StringFormatter.capitalize(row.original.lecResponsable.firstName)}`
          : '-'
    }
  ]

  return (
    <div>
      <UserNavBar title={'Liste des centres'} icon={BuildingThreeIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<CenterCompact, CenterFilter, never, CenterVersions>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Tous les centres'}
        deleteMessage={{ name: 'centre', attributeName: 'name' }}
        columns={columns}
        sorting={{ id: 'startDate', desc: false }}
        selectable
        version={'COMPACT'}
        allowSearchParam
        onRowClick={(center, event) =>
          navigateWithEvent(constructCenterPath(Paths.CENTER_GLOBAL_VIEW_PAGE, center.id), event)
        }
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        endpoint={API.centers.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter un centre',
            fn: () => navigate(Paths.CREATE_CENTER_PAGE)
          },
          {
            type: 'multiple',
            icon: UserCheckIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Assigner un responsable secteur',
            fn: (data) => {
              const centers = data.length > 0 ? data : undefined
              if (!centers) return
              toggleModal('assign-lec-responsable', centers)
            }
          },
          {
            type: 'global',
            icon: AddUserIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Demande de poste spécial',
            fn: (data) => {
              const centers = data.length > 0 ? data : undefined
              //check if countries in the centers are the same if not toast error and return
              if (centers && centers.length > 1) {
                const countries = data.map((center) => center.journeyPlace?.country.id)
                if (new Set(countries).size !== 1) {
                  toast.error('Les centres doivent être dans le même pays')
                  return
                }
              }
              toggleModal('request-staff', centers)
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter la liste',
            fn: async (centers) => {
              const ids = centers.map(({ id }) => id)
              await downloadDocuments({ ids, endpoint: API.centers.exportExcel.url })
            }
          },
          {
            type: 'multiple',
            icon: PrintIcon,
            refetch: true,
            variant: 'ghost',
            label: "Imprimer les listes d'appel",
            fn: async (centers) => {
              const ids = centers.map(({ id }) => id)
              await downloadDocuments({ ids, endpoint: API.centers.printCallLists.url })
            }
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onReset={resetFilters}
        onFilterChange={onFilterChange}
        onClose={() => closeModel('filters')}
        onApply={() => onSelectedFilterChange({ ...filter })}
      >
        <CentersFilter mode="center" filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      <Modal
        isOpen={modalState['request-staff']}
        title={
          selectedData && selectedData.length > 0
            ? 'Créer une demande de poste spécial pour les centres suivants :'
            : 'Créer une demande de poste spécial'
        }
        icon={AddUsersTwoIcon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => closeModel('request-staff')}
      >
        <RequestStaffForm
          centers={selectedData ?? []}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('request-staff'),
            onConfirm: async (values) => {
              const centerIds = selectedData?.map((center) => center.id)
              await requestStaffsMutation({ ...values, ids: centerIds, isSpecialRequest: true })
            }
          })}
        />
      </Modal>

      <Modal
        isOpen={modalState['assign-lec-responsable']}
        title={'Assigner un responsable secteur'}
        icon={UserTwoIcon}
        hasBorderBottom
        hideActions
        size="medium"
        onCancel={() => closeModel('assign-lec-responsable')}
      >
        <AssignLecResponsableForm
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('assign-lec-responsable'),
            onConfirm: async (values) => {
              const centerIds = selectedData?.map((center) => center.id)
              if (!centerIds) return
              await assignLecResponsableMutation({
                centerIds,
                lecResponsableId: values.lecResponsableId
              })
            }
          })}
        />
      </Modal>
    </div>
  )
}

export default CenterListPage
